import { useHotkeys } from '@mantine/hooks'
import { useAtomValue } from 'jotai'
import { useCallback } from 'react'

import { Button } from '@/components/button.tsx'
import {
  faBackwardStep,
  faForwardStep,
  faPause,
  faPlay,
  faStop,
} from '@fortawesome/free-solid-svg-icons'

import { RundownPermission, RunnerState } from '@rundown-studio/types'
import eventBus from '../../../utils/eventBus.js'
import { useRundownPermission } from '../../permissions/lib/use-rundown-permission.ts'

import { isRunningAtom, runnerStateAtom } from '@/store/runner.store.js'
import {
  isFirstCueAtom,
  isRunnerLoadingAtom,
  nextCueIdAtom,
  useTransportActions,
} from './transport.ts'

import { TransportCountdown } from './TransportCountdown.tsx'
import { TransportProgressBar } from './TransportProgressBar.tsx'
import { TransportStateIndicator } from './TransportStateIndicator.tsx'
import { TransportTimeOperation } from './TransportTimeOperation.tsx'

export function TransportControls() {
  const runnerState = useAtomValue(runnerStateAtom)
  const isRunning = useAtomValue(isRunningAtom)
  const isRunnerLoading = useAtomValue(isRunnerLoadingAtom)

  const writeAccess = useRundownPermission({
    permission: RundownPermission.EDIT,
  })

  const readonly = !writeAccess

  const isShowCaller = useRundownPermission({
    permission: RundownPermission.SHOWCALL,
  })

  const nextCueId = useAtomValue(nextCueIdAtom)
  const isFirstCue = useAtomValue(isFirstCueAtom)
  const isLastCue = !nextCueId

  const {
    onPlay,
    onPause,
    onPrev,
    onNext,
    onCreateRunner: onStartShow,
    onUnsetRunner: onResetShow,
    onEndRunner: onEndShow,
    onTimeOperation,
  } = useTransportActions()

  const handleSpace = useCallback(() => {
    if (!isShowCaller) return
    if (!isRunning) return onStartShow()
    if (isRunning && nextCueId) onNext()
    else if (isRunning && !nextCueId) onEndShow()
  }, [isRunning, isShowCaller, nextCueId, onEndShow, onNext, onStartShow])

  // Handle Keyboard shortcuts
  useHotkeys([
    ['Space', handleSpace],
    ['mod + Z', () => writeAccess && eventBus.$emit('openTrashModal')],
  ])

  return (
    <div className="flex flex-col justify-between h-full">
      <div className="flex-none flex gap-1 h-9">
        {/* Current Cue */}

        <div className="h-9 flex items-center gap-3 pr-3 leading-0">
          <div className="w-4 text-center">
            <TransportStateIndicator />
          </div>

          <TransportCountdown />
        </div>

        {/* Subtract Time */}
        <TransportTimeOperation
          operation="subtract"
          readonly={readonly}
          loading={isRunnerLoading}
          runnerState={runnerState}
          isShowCaller={isShowCaller}
          onTimeOperation={onTimeOperation}
        />

        <Button
          onClick={onPrev}
          toolTip="Back to the previous cue"
          icon={faBackwardStep}
          colour="dark"
          buttonClass="w-full"
          loading={isRunnerLoading}
          disabled={
            readonly ||
            runnerState !== RunnerState.ONAIR ||
            isRunnerLoading ||
            isFirstCue ||
            !isShowCaller
          }
        />

        {/* Pause / Play */}
        <Button
          onClick={isRunning ? onPause : onPlay}
          colour="dark"
          wrapperClass="flex-none"
          buttonClass="w-24!"
          loading={isRunnerLoading}
          disabled={
            readonly ||
            isRunnerLoading ||
            runnerState !== RunnerState.ONAIR ||
            !isShowCaller
          }
          icon={isRunning ? faPause : faPlay}
          toolTip={isRunning ? 'Pause' : 'Resume'}
        />

        {/* Start / Next / End / Reset */}
        {(runnerState === RunnerState.PRESHOW && (
          <Button
            onClick={onStartShow}
            text="Start show"
            toolTip="Start the show, will start timer on selected or first cue"
            colour="red"
            wrapperClass="grow"
            buttonClass="w-full"
            loading={isRunnerLoading}
            disabled={readonly || isRunnerLoading || !isShowCaller}
          />
        )) ||
          (runnerState === RunnerState.ONAIR && !isLastCue && (
            <Button
              onClick={onNext}
              text="Next cue"
              toolTip="Advance show to the next selected cue"
              icon={faForwardStep}
              colour="dark"
              wrapperClass="grow"
              buttonClass="w-full"
              loading={isRunnerLoading}
              disabled={readonly || isRunnerLoading || !isShowCaller}
            />
          )) ||
          (runnerState === RunnerState.ONAIR && isLastCue && (
            <Button
              onClick={onEndShow}
              text="End show"
              toolTip="End the show, stop timer and keep elapsed times on screen"
              icon={faStop}
              colour="dark"
              wrapperClass="grow"
              buttonClass="w-full"
              loading={isRunnerLoading}
              disabled={readonly || isRunnerLoading || !isShowCaller}
            />
          )) ||
          (runnerState === RunnerState.ENDED && (
            <Button
              onClick={onResetShow}
              text="Reset show"
              toolTip="End the show, stop timer and keep elapsed times on screen"
              icon={faBackwardStep}
              colour="dark"
              wrapperClass="grow"
              buttonClass="w-full"
              loading={isRunnerLoading}
              disabled={readonly || isRunnerLoading || !isShowCaller}
            />
          ))}

        {/* Add Time */}
        <TransportTimeOperation
          operation="add"
          readonly={readonly}
          loading={isRunnerLoading}
          runnerState={runnerState}
          isShowCaller={isShowCaller}
          onTimeOperation={onTimeOperation}
        />
      </div>

      <TransportProgressBar />
    </div>
  )
}
